// BGP Details Data
import React from 'react';
import { GenericTableColumn } from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/Icon_Edit.svg';
import { ReactComponent as IconDelete } from '../../../../shared/assets/Icons/Icon_Delete.svg';
import { buildStatusLabel, getStatusVariant } from '../../../helpers/bgpValidation';

export interface BgpNeighbor {
  neighborAddress: string;
  // Free-text label the operator sets when creating/editing the session. Optional
  // by contract, so consumers must treat empty/whitespace as "no description".
  description?: string;
  peerAs: string;
  interfaceAddress: string;
  localAs: string;
  operatingMode: 'Enterprise' | 'Telco';
  status: 'Active' | 'Inactive';
  statusChangedAt?: number | Date;
  // Cluster mode fields
  primaryStatus?: string;
  primaryStatusChangedAt?: number | Date;
  secondaryStatus?: string;
  secondaryStatusChangedAt?: number | Date;
  // Reflector liveness (cluster only). false = that side's reflector stopped
  // sending keepalives, so its session/BFD fields below are frozen (stale) and
  // must not be rendered as live state. null/undefined = unknown (no data).
  primaryReflectorOnline?: boolean | null;
  secondaryReflectorOnline?: boolean | null;
  primaryReflectorLastUpdate?: string | number | Date | null;
  secondaryReflectorLastUpdate?: string | number | Date | null;
  // BFD config (user-configured via dashboard)
  BFD?: boolean;
  // bfdInterval == BFD desired_min_tx_interval (ms). Kept for retro-compat;
  // also acts as the fallback for bfdRequiredMinRxInterval when the RX value
  // is not set explicitly.
  bfdInterval?: number;
  // bfdRequiredMinRxInterval == BFD required_min_rx_interval (ms). When
  // undefined/null, consumers must fall back to bfdInterval.
  bfdRequiredMinRxInterval?: number;
  bfdMultiplier?: number;
  // BFD telemetry -- primary reflector (or standalone)
  bfdLocalState?: string;
  bfdRemoteState?: string;
  bfdStatusChangedAt?: number;
  bfdDaemonTxInterval?: number;
  bfdDaemonRxInterval?: number;
  bfdDaemonMultiplier?: number;
  bfdDaemonMultiHop?: boolean;
  // BFD negotiated / remote (RFC 5880 §6.8) -- primary reflector (or standalone)
  bfdNegotiatedTxInterval?: number;
  bfdNegotiatedDetectionTime?: number;
  bfdRemoteTxInterval?: number;
  bfdRemoteRxInterval?: number;
  bfdRemoteMultiplier?: number;
  bfdRemoteDiscriminator?: string;
  // BFD telemetry -- secondary reflector (cluster only)
  secondaryBfdLocalState?: string;
  secondaryBfdRemoteState?: string;
  secondaryBfdStatusChangedAt?: number;
  secondaryBfdDaemonTxInterval?: number;
  secondaryBfdDaemonRxInterval?: number;
  secondaryBfdDaemonMultiplier?: number;
  secondaryBfdDaemonMultiHop?: boolean;
  // BFD negotiated / remote (RFC 5880 §6.8) -- secondary reflector (cluster only)
  secondaryBfdNegotiatedTxInterval?: number;
  secondaryBfdNegotiatedDetectionTime?: number;
  secondaryBfdRemoteTxInterval?: number;
  secondaryBfdRemoteRxInterval?: number;
  secondaryBfdRemoteMultiplier?: number;
  secondaryBfdRemoteDiscriminator?: string;
  // Prefix counters -- primary reflector (or standalone)
  prefixesReceived?: number;
  prefixesAccepted?: number;
  prefixesAdvertised?: number;
  // Prefix counters -- secondary reflector (cluster only)
  secondaryPrefixesReceived?: number;
  secondaryPrefixesAccepted?: number;
  secondaryPrefixesAdvertised?: number;
}

export const bgpNeighborsColumns: GenericTableColumn<BgpNeighbor>[] = [
  {
    // `accessor` is kept so the column still sorts by address; `renderCell` only
    // changes how the cell is drawn.
    header: 'Peer IP',
    accessor: 'neighborAddress',
    renderCell: (row) => {
      const description = row.description?.trim();
      return (
        <div className="flex flex-col gap-0.5">
          <span>{row.neighborAddress}</span>
          {/* Rendered only when set, so rows without a description keep the
              single-line height the rest of the table assumes. */}
          {description && (
            <span
              className="text-[10px] text-semantic-text-secondary truncate max-w-[180px]"
              title={description}
            >
              {description}
            </span>
          )}
        </div>
      );
    },
  },
  {
    header: 'ASN (Local / Peer)',
    accessor: 'localAs',
    renderCell: (row) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-mono whitespace-nowrap">{row.localAs} / {row.peerAs}</span>
      </div>
    ),
  },
  {
    header: 'Mode',
    accessor: 'operatingMode',
    renderCell: (row) => {
      const mode = row.operatingMode?.toLowerCase() || '';
      const displayMode = mode === 'enterprise' ? 'Enterprise' : 'Telco';
      return (
        <GenericTag
          label={displayMode}
          variant={mode === 'enterprise' ? 'neutral' : 'warning'}
        />
      );
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    renderCell: (row) => {
      const label = buildStatusLabel(row.status, row.statusChangedAt);
      const variant = getStatusVariant(row.status);
      
      return (
        <GenericTag
          label={label}
          variant={variant}
        />
      );
    },
  },
  {
    header: 'Actions',
    accessor: 'status',
    renderCell: (row) => (
      <div className="flex items-center gap-2">
        <GenericButton
          isIconOnly
          icon={<IconEdit className="w-8 h-8" />}
          variant="secondary"
          shape="filled"
          size="sm"
          aria-label="Edit"
          onClick={() => { console.log('Edit neighbor:', row); }}
        />
        <GenericButton
          isIconOnly
          icon={<IconDelete className="w-6 h-6" />}
          variant="danger"
          shape="filled"
          size="sm"
          aria-label="Delete"
          onClick={() => { console.log('Delete neighbor:', row); }}
        />
      </div>
    ),
  },
];
